// =============================================================================
// PLANT LAYOUT — CSV PARSER & DATA BUILDER
// =============================================================================
// Parses Hincas_Navabuena_v2.csv (Windows-1252, ';' separator, ',' decimal)
// and builds an in-memory dataset grouped by parcela → tracker → piles.

import { utm25830ToLatLon } from './utmToLatLon'
import type {
  PileData,
  TrackerData,
  ParcelaStats,
  PlantDataset,
  TrackerStatus,
  PierType,
  PierColor,
  PileSteps,
  PileStepCode,
} from '@/types/plant'

// ---------------------------------------------------------------------------
// Demo status assignment (deterministic, no backend required)
// ---------------------------------------------------------------------------
const DEMO_STATUS_POOL: TrackerStatus[] = [
  'NO_INICIADA', 'NO_INICIADA', 'NO_INICIADA',
  'EN_CURSO', 'EN_CURSO',
  'FINALIZADA',
  'BLOQUEADA',
]

function getDemoStatus(orden: number): TrackerStatus {
  return DEMO_STATUS_POOL[orden % DEMO_STATUS_POOL.length]
}

function getDemoProgress(status: TrackerStatus, orden: number): number {
  switch (status) {
    case 'NO_INICIADA': return 0
    case 'FINALIZADA':  return 100
    case 'BLOQUEADA':   return ((orden * 17) % 40) + 5      // 5-44%
    case 'EN_CURSO':    return ((orden * 13) % 80) + 10     // 10-89%
  }
}

function getDemoPileStatus(trackerStatus: TrackerStatus, posicion: number, numHincas: number): TrackerStatus {
  if (trackerStatus === 'NO_INICIADA') return 'NO_INICIADA'
  if (trackerStatus === 'FINALIZADA')  return 'FINALIZADA'
  if (trackerStatus === 'BLOQUEADA')   return posicion <= 3 ? 'FINALIZADA' : 'BLOQUEADA'
  // EN_CURSO: piles at the beginning are done, middle ones in progress
  const threshold = Math.floor(numHincas * 0.5)
  if (posicion <= threshold)         return 'FINALIZADA'
  if (posicion === threshold + 1)    return 'EN_CURSO'
  return 'NO_INICIADA'
}

// ---------------------------------------------------------------------------
// Load overrides from localStorage (user edits in demo mode)
// ---------------------------------------------------------------------------
const LS_KEY = 'navabuena-tracker-status'

export function loadTrackerOverrides(): Record<string, { status: TrackerStatus; progressPct: number }> {
  try {
    const raw = localStorage.getItem(LS_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

export function saveTrackerOverride(codigoTracker: string, status: TrackerStatus, progressPct: number): void {
  try {
    const overrides = loadTrackerOverrides()
    overrides[codigoTracker] = { status, progressPct }
    localStorage.setItem(LS_KEY, JSON.stringify(overrides))
  } catch {
    // ignore storage errors
  }
}

// ---------------------------------------------------------------------------
// Pile state overrides (individual pile type changes)
// ---------------------------------------------------------------------------
const LS_KEY_PILES = 'navabuena-pile-state'

export interface PileOverride {
  partNo:        string
  pierType:      import('@/types/plant').PierType
  esMotora:      boolean
  overallStatus?: import('@/types/plant').TrackerStatus
  steps?:         import('@/types/plant').PileSteps
}

export function loadPileOverrides(): Record<string, PileOverride> {
  try {
    const raw = localStorage.getItem(LS_KEY_PILES)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

export function savePileOverride(codigoHinca: string, override: PileOverride): void {
  try {
    const overrides = loadPileOverrides()
    overrides[codigoHinca] = override
    localStorage.setItem(LS_KEY_PILES, JSON.stringify(overrides))
  } catch {
    // ignore
  }
}

export function savePileStatusOverride(codigoHinca: string, overallStatus: import('@/types/plant').TrackerStatus, steps?: import('@/types/plant').PileSteps): void {
  try {
    const overrides = loadPileOverrides()
    if (!overrides[codigoHinca]) {
      overrides[codigoHinca] = { partNo: '', pierType: 'SAP' as import('@/types/plant').PierType, esMotora: false }
    }
    overrides[codigoHinca].overallStatus = overallStatus
    if (steps !== undefined) overrides[codigoHinca].steps = steps
    localStorage.setItem(LS_KEY_PILES, JSON.stringify(overrides))
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Batch save helpers — write all overrides in a single localStorage operation
// to avoid O(N) writes when changing state of many elements at once.
// ---------------------------------------------------------------------------

export function batchSaveTrackerOverrides(
  batch: Record<string, { status: import('@/types/plant').TrackerStatus; progressPct: number }>
): void {
  try {
    const existing = loadTrackerOverrides()
    Object.assign(existing, batch)
    localStorage.setItem(LS_KEY, JSON.stringify(existing))
  } catch {
    // ignore storage errors
  }
}

export function batchSavePileStatusOverrides(
  batch: Record<string, import('@/types/plant').TrackerStatus>
): void {
  try {
    const existing = loadPileOverrides()
    for (const [codigoHinca, status] of Object.entries(batch)) {
      if (!existing[codigoHinca]) {
        existing[codigoHinca] = { partNo: '', pierType: 'SAP' as import('@/types/plant').PierType, esMotora: false }
      }
      existing[codigoHinca].overallStatus = status
    }
    localStorage.setItem(LS_KEY_PILES, JSON.stringify(existing))
  } catch {
    // ignore storage errors
  }
}

// ---------------------------------------------------------------------------
// CSV parsing
// ---------------------------------------------------------------------------
function parseNumber(s: string): number {
  // Replace comma decimal separator → period
  return parseFloat(s.replace(',', '.'))
}

function parseBool(s: string): boolean {
  const t = s.trim().toLowerCase()
  return t === 'sí' || t === 'si' || t === 's\xed' || t === 'yes' || t === 'true' || t === '1'
}

/**
 * Fetch and parse the plant CSV file.
 * Returns a full PlantDataset with all trackers grouped by parcela.
 * Progress callback receives 0-100.
 */
export async function loadPlantData(
  onProgress?: (pct: number) => void,
): Promise<PlantDataset> {
  // Fetch with Windows-1252 decoding
  const response = await fetch('/data/hincas.csv')
  if (!response.ok) throw new Error(`Failed to fetch plant data: ${response.status}`)

  const buffer = await response.arrayBuffer()
  const text   = new TextDecoder('windows-1252').decode(buffer)
  onProgress?.(10)

  const lines = text.split('\n')
  const total = lines.length

  // Load user overrides from localStorage
  const overrides = loadTrackerOverrides()

  // Intermediate storage: trackerCode → PileData[]
  const pilesMap = new Map<string, PileData[]>()

  for (let i = 1; i < total; i++) {  // skip header row
    const line = lines[i].trim()
    if (!line) continue

    const cols = line.split(';')
    if (cols.length < 25) continue

    const x      = parseNumber(cols[0])
    const y      = parseNumber(cols[1])
    if (isNaN(x) || isNaN(y)) continue

    const [lng, lat] = utm25830ToLatLon(x, y)

    const pile: PileData = {
      codigoHinca:   cols[24].trim(),
      codigoTracker: cols[23].trim(),
      x,
      y,
      lng,
      lat,
      parcela:       parseInt(cols[2]),
      subproyecto:   cols[3].trim(),
      powerStation:  cols[4].trim(),
      ncu:           parseInt(cols[5]),
      orden:         parseInt(cols[7]),
      strings:       cols[8].trim(),
      numHincas:     parseInt(cols[9]),
      posicion:      parseInt(cols[10]),
      esInicial:     parseBool(cols[11]),
      esFinal:       parseBool(cols[12]),
      esMotora:      parseBool(cols[13]),
      tieneDamper:   parseBool(cols[14]),
      numDampers:    parseInt(cols[15]) || 0,
      layerHinca:    cols[16].trim(),
      symbol:        cols[17].trim(),
      pierType:      cols[18].trim() as PierType,
      section:       cols[19].trim(),
      lengthMm:      parseNumber(cols[20]) || 0,
      partNo:        cols[21].trim(),
      color:         cols[22].trim() as PierColor,
      overallStatus: 'NO_INICIADA' as TrackerStatus,  // will be set after tracker grouping
      steps:         {} as PileSteps,
    }

    const arr = pilesMap.get(pile.codigoTracker) ?? []
    arr.push(pile)
    pilesMap.set(pile.codigoTracker, arr)

    // Report progress every 5000 rows
    if (i % 5000 === 0) {
      onProgress?.(10 + Math.round((i / total) * 75))
      // Yield to keep UI responsive
      await new Promise<void>((r) => setTimeout(r, 0))
    }
  }

  onProgress?.(85)

  // ---------------------------------------------------------------------------
  // Build TrackerData objects
  // ---------------------------------------------------------------------------
  const trackersByParcela = new Map<number, TrackerData[]>()

  for (const [codigoTracker, piles] of pilesMap) {
    // Sort piles by position
    piles.sort((a, b) => a.posicion - b.posicion)

    const first = piles[0]

    // Bounding box
    let minLng = Infinity, maxLng = -Infinity
    let minLat = Infinity, maxLat = -Infinity
    for (const p of piles) {
      if (p.lng < minLng) minLng = p.lng
      if (p.lng > maxLng) maxLng = p.lng
      if (p.lat < minLat) minLat = p.lat
      if (p.lat > maxLat) maxLat = p.lat
    }

    // Center point (median pile position)
    const mid = piles[Math.floor(piles.length / 2)]

    // Demo status
    const override = overrides[codigoTracker]
    const demoStatus = getDemoStatus(first.orden)
    const status      = override?.status      ?? demoStatus
    const progressPct = override?.progressPct ?? getDemoProgress(demoStatus, first.orden)

    const tracker: TrackerData = {
      codigoTracker,
      parcela:      first.parcela,
      subproyecto:  first.subproyecto,
      powerStation: first.powerStation,
      ncu:          first.ncu,
      orden:        first.orden,
      strings:      first.strings,
      numHincas:    first.numHincas,
      piles,
      centerLng:    mid.lng,
      centerLat:    mid.lat,
      minLng,
      maxLng,
      minLat,
      maxLat,
      status,
      progressPct,
    }

    const list = trackersByParcela.get(first.parcela) ?? []
    list.push(tracker)
    trackersByParcela.set(first.parcela, list)
  }

  // Sort trackers within each parcela by orden
  for (const [, list] of trackersByParcela) {
    list.sort((a, b) => a.orden - b.orden)
  }

  // Apply pile overrides from localStorage
  const pileOverrides = loadPileOverrides()
  for (const trackers of trackersByParcela.values()) {
    for (const tracker of trackers) {
      for (const pile of tracker.piles) {
        const over = pileOverrides[pile.codigoHinca]
        if (over) {
          pile.partNo   = over.partNo
          pile.pierType = over.pierType
          pile.esMotora = over.esMotora
          if (over.overallStatus !== undefined) {
            pile.overallStatus = over.overallStatus
          }
          if (over.steps !== undefined) {
            pile.steps = over.steps
          }
        }
      }
    }
  }

  // Set demo overallStatus for piles without overrides
  for (const trackers of trackersByParcela.values()) {
    for (const tracker of trackers) {
      for (const pile of tracker.piles) {
        if (pile.overallStatus === 'NO_INICIADA' && tracker.status !== 'NO_INICIADA') {
          pile.overallStatus = getDemoPileStatus(tracker.status, pile.posicion, tracker.numHincas)
        }
      }
    }
  }

  onProgress?.(92)

  // ---------------------------------------------------------------------------
  // Build ParcelaStats
  // ---------------------------------------------------------------------------
  const parcelaStats = new Map<number, ParcelaStats>()

  for (const [parcela, trackers] of trackersByParcela) {
    let noIniciada = 0, enCurso = 0, finalizada = 0, bloqueada = 0
    let totalProgress = 0
    let minLng = Infinity, maxLng = -Infinity
    let minLat = Infinity, maxLat = -Infinity

    for (const t of trackers) {
      switch (t.status) {
        case 'NO_INICIADA': noIniciada++;   break
        case 'EN_CURSO':    enCurso++;      break
        case 'FINALIZADA':  finalizada++;   break
        case 'BLOQUEADA':   bloqueada++;    break
      }
      totalProgress += t.progressPct
      if (t.minLng < minLng) minLng = t.minLng
      if (t.maxLng > maxLng) maxLng = t.maxLng
      if (t.minLat < minLat) minLat = t.minLat
      if (t.maxLat > maxLat) maxLat = t.maxLat
    }

    parcelaStats.set(parcela, {
      parcela,
      totalTrackers: trackers.length,
      noIniciada,
      enCurso,
      finalizada,
      bloqueada,
      progressPct:   trackers.length > 0 ? Math.round(totalProgress / trackers.length) : 0,
      bounds:        [minLng, minLat, maxLng, maxLat],
    })
  }

  // Overall bounds
  let gMinLng = Infinity, gMaxLng = -Infinity
  let gMinLat = Infinity, gMaxLat = -Infinity
  for (const [, s] of parcelaStats) {
    if (s.bounds[0] < gMinLng) gMinLng = s.bounds[0]
    if (s.bounds[2] > gMaxLng) gMaxLng = s.bounds[2]
    if (s.bounds[1] < gMinLat) gMinLat = s.bounds[1]
    if (s.bounds[3] > gMaxLat) gMaxLat = s.bounds[3]
  }

  onProgress?.(100)

  const totalTrackers = [...trackersByParcela.values()].reduce((acc, arr) => acc + arr.length, 0)
  const totalPiles    = [...pilesMap.values()].reduce((acc, arr) => acc + arr.length, 0)

  return {
    trackersByParcela,
    parcelaStats,
    totalTrackers,
    totalPiles,
    bounds: [gMinLng, gMinLat, gMaxLng, gMaxLat],
  }
}

// ---------------------------------------------------------------------------
// GeoJSON generators (called per-parcela to keep things fast)
// ---------------------------------------------------------------------------

interface GeoFeatureCollection {
  type: 'FeatureCollection'
  features: Array<{
    type: 'Feature'
    id?: number
    geometry: { type: string; coordinates: unknown }
    properties: Record<string, unknown>
  }>
}

/** Generate LineString GeoJSON for all trackers in a parcela */
export function buildTrackerGeoJSON(trackers: TrackerData[]): GeoFeatureCollection {
  return {
    type: 'FeatureCollection',
    features: trackers.map((t, idx) => ({
      type: 'Feature',
      id: idx,
      geometry: {
        type: 'LineString',
        coordinates: t.piles.map(p => [p.lng, p.lat]),
      },
      properties: {
        codigoTracker: t.codigoTracker,
        parcela:       t.parcela,
        powerStation:  t.powerStation,
        ncu:           t.ncu,
        orden:         t.orden,
        strings:       t.strings,
        numHincas:     t.numHincas,
        status:        t.status,
        progressPct:   t.progressPct,
      },
    })),
  }
}

/** Generate Point GeoJSON for all piles in the given trackers */
export function buildPilesGeoJSON(trackers: TrackerData[]): GeoFeatureCollection {
  const features: GeoFeatureCollection['features'] = []
  for (const t of trackers) {
    for (const p of t.piles) {
      features.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [p.lng, p.lat],
        },
        properties: {
          codigoHinca:   p.codigoHinca,
          codigoTracker: p.codigoTracker,
          posicion:      p.posicion,
          pierType:      p.pierType,
          esMotora:      p.esMotora,
          tieneDamper:   p.tieneDamper,
          numDampers:    p.numDampers,
          color:         p.color,
          parcela:       p.parcela,
          trackerStatus: t.status,
          overallStatus: p.overallStatus,
        },
      })
    }
  }
  return { type: 'FeatureCollection', features }
}

// ---------------------------------------------------------------------------
// Generic element step overrides (zanjas_bt, inversores, vallado, subestaciones, viales)
// Structure: { [layerKey]: { [featureId]: stepCode } }
// ---------------------------------------------------------------------------
const LS_KEY_GENERIC = 'nv_generic_step_overrides'

export function loadGenericStepOverrides(): Record<string, Record<string, string>> {
  try {
    const raw = localStorage.getItem(LS_KEY_GENERIC)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

export function saveGenericStepOverride(layerKey: string, featureId: string, stepCode: string): void {
  try {
    const overrides = loadGenericStepOverrides()
    if (!overrides[layerKey]) overrides[layerKey] = {}
    overrides[layerKey][featureId] = stepCode
    localStorage.setItem(LS_KEY_GENERIC, JSON.stringify(overrides))
  } catch {
    // ignore storage errors
  }
}
